import React, { useMemo } from "react";
import { Table } from "antd";
import { Spinner } from "react-bootstrap";
import { Icon } from "@vds/icons";
import { TextLink } from "@vds/buttons";
import { Body, Title } from "@vds/typography";
import { Tooltip } from "@mui/material";
import { NON_NUMERIC_REGEX, USER_GROUP_DETAILS_CONSTANTS, TABLE_COLUMNS } from "../../constants/constants";
import { sortAlphabetically, SORT_DIRECTIONS } from "../../utility/sortingUtils";
import "./UserGroupDetailsTable.scss";

const formatPhone = (phone) => {
  if (!phone) return "-";
  const digits = phone.replace(NON_NUMERIC_REGEX, "");
  if (digits.length >= 10) {
    const lastTen = digits.slice(-10);
    return `${lastTen.slice(0, 3)}-${lastTen.slice(3, 6)}-${lastTen.slice(6)}`;
  }
  return phone;
};

function UserGroupDetailsTable({ groupMembers, onDelete, onUserDetailsClick, loading = false }) {
  const columns = useMemo(() => [
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          {TABLE_COLUMNS.NAME}
        </Title>
      ),
      dataIndex: "name",
      key: "name",
      width: "25%",
      sorter: (a, b) => sortAlphabetically(a, b, "name"),
      sortDirections: SORT_DIRECTIONS,
      render: (text, record) =>
        onUserDetailsClick ? (
          <TextLink
            type="standAlone"
            size="large"
            className="user-group-details-table-link"
            onClick={() => onUserDetailsClick({ ...record._raw, isActiveUser: true })}
          >
            <Body size="large" viewport="desktop" primitive="span">{text}</Body>
          </TextLink>
        ) : (
          <Body size="large" viewport="desktop" primitive="span">{text}</Body>
        ),
    },
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          {TABLE_COLUMNS.PHONE_NUMBER}
        </Title>
      ),
      dataIndex: "phone",
      key: "phone",
      width: "20%",
      sorter: (a, b) => sortAlphabetically(a, b, "phone"),
      sortDirections: SORT_DIRECTIONS,
    },
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          {TABLE_COLUMNS.EMAIL}
        </Title>
      ),
      dataIndex: "email",
      key: "email",
      width: "47%",
      sorter: (a, b) => sortAlphabetically(a, b, "email"),
      sortDirections: SORT_DIRECTIONS,
    },
    {
      title: (
        <Title size="small" bold primitive="h2" viewport="desktop">
          {TABLE_COLUMNS.ACTIONS}
        </Title>
      ),
      dataIndex: "actions",
      key: "actions",
      width: "8%",
      render: (text, record) => (
        <Tooltip title={USER_GROUP_DETAILS_CONSTANTS.TOOLTIP_DELETE_USER} arrow placement="top">
          <div
            onClick={() => onDelete(record._raw.id, record.name)}
            className="user-group-details-table-delete"
          >
            <Icon name="trash" size="large" color="#000000" ariaHidden={true} />
          </div>
        </Tooltip>
      ),
    },
  ], [onDelete, onUserDetailsClick]);

  const dataSource = useMemo(() =>
    groupMembers.map((user, index) => ({
      key: user.id || `user-${index}`,
      name: `${user.firstname || ""} ${user.lastname || ""}`.trim(),
      phone: formatPhone(user.mdn),
      email: user.email || "",
      _raw: user,
    })),
    [groupMembers]
  );

  return (
    <div className="user-group-details-table-wrapper">
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={false}
        locale={loading ? { emptyText: <Spinner animation="border" variant="dark" className="users-loading-spinner" /> } : undefined}
        pagination={false}
        showSorterTooltip={false}
      />
    </div>
  );
}

export default UserGroupDetailsTable;
